import { createContext, useContext, useEffect, useState, useCallback, useMemo } from "react";
import { api } from "../lib/api";

const AuthContext = createContext(null);

export const useAuth = () => useContext(AuthContext);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null); // null = checking, false = anon, obj = user
  const [authOpen, setAuthOpen] = useState(false);

  const checkAuth = useCallback(async () => {
    try {
      const { data } = await api.get("/auth/me");
      setUser(data && data.authenticated === false ? false : data);
    } catch {
      setUser(false);
    }
  }, []);

  useEffect(() => {
    if (window.location.hash?.includes("session_id=")) return; // AuthCallback handles it
    checkAuth();
  }, [checkAuth]);

  useEffect(() => {
    if (!user || !user.user_id) return;
    try {
      const tz = Intl.DateTimeFormat().resolvedOptions().timeZone;
      if (tz && user.timezone !== tz) api.post("/users/timezone", { timezone: tz }).catch(() => {});
    } catch { /* ignore */ }
  }, [user]);

  const login = useCallback(async (email, password) => {
    const { data } = await api.post("/auth/login", { email, password });
    if (data && data.two_fa_required) return data; // needs email code before session
    setUser(data);
    return data;
  }, []);
  const verify2FA = useCallback(async (challengeId, code, rememberDevice = false) => {
    const { data } = await api.post("/auth/verify-2fa", { challenge_id: challengeId, code, remember_device: rememberDevice });
    setUser(data);
    return data;
  }, []);
  const register = useCallback(async (name, email, password, seasonal_emails = true) => {
    const lang = (typeof window !== "undefined" && localStorage.getItem("cp_lang") === "fr") ? "fr" : "en";
    const { data } = await api.post("/auth/register", { name, email, password, lang, seasonal_emails });
    setUser(data);
    return data;
  }, []);
  const logout = useCallback(async () => {
    await api.post("/auth/logout");
    setUser(false);
  }, []);
  const updateProfile = useCallback(async (payload) => {
    const { data } = await api.put("/auth/me", payload);
    setUser(data);
    return data;
  }, []);
  const impersonate = useCallback(async (userId) => {
    await api.post(`/admin/impersonate/${userId}`);
    const { data } = await api.get("/auth/me"); // includes impersonating + impersonator_id
    setUser(data);
    return data;
  }, []);
  const stopImpersonation = useCallback(async () => {
    await api.post("/auth/stop-impersonation");
    const { data } = await api.get("/auth/me");
    setUser(data);
    return data;
  }, []);
  const googleLogin = useCallback(() => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + "/dashboard";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  }, []);

  const value = useMemo(
    () => ({ user, setUser, checkAuth, login, verify2FA, register, logout, googleLogin, updateProfile, impersonate, stopImpersonation, authOpen, setAuthOpen }),
    [user, authOpen, checkAuth, login, verify2FA, register, logout, googleLogin, updateProfile, impersonate, stopImpersonation]
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}
